// The Tysiąc table (.ai/tysiac.md): the game of 1000/rules.ts, the 2 computer players' timing (the redeal, each word of
// the bidding, the musik, each card given, the contract, every card played), the player's own steps, the clock
// and the score window after each deal. The computer waits while the Pause window is open or the tab is hidden. No
// Undo (as in Kierki).
import { useSyncExternalStore } from 'react';
import {
  answerRedeal, botBid, botContract, botGive, botPlay, botRedeal, cardKey, collectTrick, giveCard, giveTo, placeBid,
  playCard, setContract, startGame, startHand, takeMusik, type HCard, type State1000,
} from './rules';
import { t } from '../i18n';
import { SLOW } from '../hearts/store';
import { drawNames, fixedNames, nameList, type SeatNames } from '../hearts/names';

/** A computer player's card, and the full trick before it is taken (as Kierki). */
export const BOT_MS = 700;
export const TRICK_MS = 1200;
export const COLLECT_MS = 420;
/** A computer player's word in the bidding (and its answer to a redeal). */
export const BID_MS = 950;
/** The musik lies face up this long before the declarer takes it (everyone sees it), then flies to him. */
export const MUSIK_SHOW_MS = 1900;
export const MUSIK_FLY_MS = 520;
/** A computer declarer thinks this long before each card it gives, and before its contract. */
export const GIVE_MS = 1000;
export const GIVE_FLY_MS = 480;
export const CONTRACT_MS = 1100;
/** The deal (Table1000.tsx useDealAnimation): 24 cards round the table and to the musik. */
export const DEAL_STEP_MS = 70;
export const DEAL_FLIGHT_MS = 360;
export const DEAL_MS = 23 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 150;

/** A card the declarer gave: the view flies it. */
export interface Gift {
  readonly from: number;
  readonly to: number;
  readonly card: HCard;
}

export interface TableState {
  readonly game: State1000 | null;
  /** Seconds of play in this game (the status bar's clock). */
  readonly seconds: number;
  /** Counts the deals shown (the view's deal animation starts on a change). */
  readonly deal: number;
  /** The full trick slides to its taker now. */
  readonly collecting: boolean;
  /** Counts the cards given (the view's flight starts on a change); the last one. */
  readonly gift: number;
  readonly lastGift: Gift | null;
  /** The cards the player played in this game (a win's `moves`). */
  readonly playedByPlayer: number;
  /** A position put in by the e2e hook (load): its end is never saved as a win. */
  readonly demo: boolean;
  readonly names: SeatNames | null;
  /** When the game began and ended (Date.now(); the score window's New game: hearts/nextGame.ts). */
  readonly startedAt: number;
  readonly endedAt: number | null;
  /** Online (online/thousand.ts): when my move runs out (Date.now() ms), then the server's bot plays it; unset offline. */
  readonly deadlineAt?: number | null;
}

export interface Store1000 {
  getState(): TableState;
  subscribe(fn: () => void): () => void;
  /** A new game to `target`: `pattern` (52 numbers, engine/shuffle.ts) gives its first deal (Retry, a report). */
  newGame(pattern: readonly number[] | null, target: number): void;
  nextHand(): void;
  /** The player answers the redeal question. */
  redeal(yes: boolean): void;
  /** The player bids `value` or passes. */
  bid(value: number | 'pass'): void;
  /** The player names the contract. */
  contract(value: number): void;
  /** A tap on a card of the hand, read by the phase: give it, play it. */
  tap(card: HCard): void;
  stop(): void;
  /** e2e only: show a position as if the game had reached it. */
  load(g: State1000): void;
  /** e2e only: the game began `ms` earlier. */
  backdate(ms: number): void;
}

export interface Hooks1000 {
  rng: () => number;
  paused: () => boolean;
  onStep?: (what: string) => void;
  /** A deal or the game ended (the score window opens). */
  onHandEnd?: (s: State1000) => void;
}

export function create1000Store(hooks: Hooks1000): Store1000 {
  let state: TableState = { game: null, seconds: 0, deal: 0, collecting: false, gift: 0, lastGift: null, playedByPlayer: 0, demo: false, names: null, startedAt: 0, endedAt: null };
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let clock: ReturnType<typeof setInterval> | null = null;
  let dealing = false;
  const set = (patch: Partial<TableState>) => {
    state = { ...state, ...patch };
    for (const fn of listeners) fn();
  };
  const step = (what: string) => hooks.onStep?.(what);
  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  /** Runs `fn` after `ms`, later again while the game is paused. */
  const after = (ms: number, fn: () => void) => {
    clearTimer();
    timer = setTimeout(function run() {
      if (hooks.paused()) {
        timer = setTimeout(run, 300);
        return;
      }
      timer = null;
      fn();
    }, ms * SLOW);
  };
  const startClock = () => {
    if (clock !== null) return;
    clock = setInterval(() => {
      const g = state.game;
      if (!g || g.phase === 'over' || g.phase === 'hand-end' || hooks.paused()) return;
      set({ seconds: state.seconds + 1 });
    }, 1000);
  };
  const stopClock = () => {
    if (clock !== null) clearInterval(clock);
    clock = null;
  };

  /** A card was given: the view flies it, the next step waits for it. */
  const gave = (g: State1000, from: number, to: number, card: HCard) => {
    step(`seat ${from} gives ${cardKey(card)} to seat ${to}`);
    set({ game: g, gift: state.gift + 1, lastGift: { from, to, card } });
    after(GIVE_FLY_MS + 120, () => advance(state.game ?? g));
  };

  /** After every change: the computer's next step, the musik, the trick taken, or the deal's end. */
  const advance = (g: State1000) => {
    set({ game: g });
    if (dealing) return;
    if (g.phase === 'redeal') {
      const seat = g.asking[0]!;
      if (seat === 0) return;
      after(BID_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'redeal' || cur.asking[0] !== seat) return;
        const yes = botRedeal(cur, seat);
        step(`seat ${seat} ${yes ? 'asks for' : 'keeps'} a redeal`);
        const next = answerRedeal(cur, seat, yes, hooks.rng)!;
        if (yes) dealt(next);
        else advance(next);
      });
      return;
    }
    if (g.phase === 'bid') {
      if (g.turn === 0) return;
      after(BID_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'bid' || cur.turn === 0) return;
        const w = botBid(cur, cur.turn);
        step(`seat ${cur.turn} ${w === 'pass' ? 'passes' : `bids ${w}`}`);
        advance(placeBid(cur, cur.turn, w)!);
      });
      return;
    }
    if (g.phase === 'musik') {
      // everyone sees the musik, then it goes to the declarer
      after(MUSIK_SHOW_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'musik') return;
        step(`musik ${cur.musik.map(cardKey).join(' ')} to seat ${cur.declarer}`);
        const next = takeMusik(cur);
        set({ game: next });
        after(MUSIK_FLY_MS + 100, () => advance(state.game ?? next));
      });
      return;
    }
    if (g.phase === 'give') {
      if (g.declarer === 0) return;
      after(GIVE_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'give' || cur.declarer === 0) return;
        const card = botGive(cur, cur.declarer);
        gave(giveCard(cur, cur.declarer, card)!, cur.declarer, giveTo(cur), card);
      });
      return;
    }
    if (g.phase === 'contract') {
      if (g.declarer === 0) return;
      after(CONTRACT_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'contract') return;
        const v = botContract(cur, cur.declarer);
        step(`seat ${cur.declarer} plays ${v}`);
        advance(setContract(cur, cur.declarer, v)!);
      });
      return;
    }
    if (g.phase === 'play' && g.turn !== 0) {
      after(BOT_MS, () => {
        const cur = state.game!;
        if (cur.phase !== 'play' || cur.turn === 0) return;
        advance(played(cur, cur.turn, botPlay(cur, cur.turn)));
      });
      return;
    }
    if (g.phase === 'trick-done') {
      after(TRICK_MS - COLLECT_MS, () => {
        set({ collecting: true });
        after(COLLECT_MS, () => {
          const cur = state.game!;
          step(`trick ${cur.gone.length / 3 + 1} to seat ${cur.winner} (${cur.trick.map((p) => cardKey(p.card)).join(' ')})`);
          set({ collecting: false });
          advance(collectTrick(cur));
        });
      });
      return;
    }
    if (g.phase === 'hand-end' || g.phase === 'over') {
      if (g.phase === 'over') set({ endedAt: Date.now() });
      const r = g.history[g.history.length - 1]!;
      step(`deal ${g.handNo + 1} (${r.contract} by ${r.declarer}${r.made ? ', made' : ''}): points ${r.points.join(' ')} (totals ${g.scores.join(' ')})`);
      hooks.onHandEnd?.(g);
    }
  };

  /** A card played (a marriage is logged). */
  const played = (g: State1000, seat: number, card: HCard): State1000 => {
    const next = playCard(g, seat, card)!;
    if (next.melds.length > g.melds.length) step(`seat ${seat} declares the marriage ${card.s}`);
    return next;
  };

  /** A new deal (or a redeal): shown at once (the view throws the cards out), played on once the deal is over. */
  const dealt = (g: State1000) => {
    dealing = true;
    set({ game: g, deal: state.deal + 1, lastGift: null });
    after(DEAL_MS, () => {
      dealing = false;
      advance(state.game ?? g);
    });
  };

  const mine = (phase: State1000['phase']) => {
    const g = state.game;
    return g && g.phase === phase && !dealing ? g : null;
  };

  return {
    getState: () => state,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    newGame(pattern, target) {
      clearTimer();
      set({ seconds: 0, collecting: false, playedByPlayer: 0, demo: false, startedAt: Date.now(), endedAt: null, names: drawNames(hooks.rng, nameList(t('hearts.names')), t('hearts.you')) ?? fixedNames(t) });
      const g = startGame(target, pattern, hooks.rng);
      step(`1000 game to ${target}, deal 1`);
      startClock();
      dealt(g);
    },
    nextHand() {
      const g = state.game;
      if (!g || g.phase !== 'hand-end') return;
      clearTimer();
      const n = startHand(g, null, hooks.rng);
      step(`deal ${n.handNo + 1}, seat ${n.opener} opens`);
      dealt(n);
    },
    redeal(yes) {
      const g = mine('redeal');
      if (!g || g.asking[0] !== 0) return;
      step(yes ? 'asked for a redeal' : 'kept the cards');
      const next = answerRedeal(g, 0, yes, hooks.rng)!;
      if (yes) dealt(next);
      else advance(next);
    },
    bid(value) {
      const g = mine('bid');
      const next = g && placeBid(g, 0, value);
      if (!next) return;
      step(value === 'pass' ? 'passed' : `bid ${value}`);
      advance(next);
    },
    contract(value) {
      const g = mine('contract');
      const next = g && setContract(g, 0, value);
      if (!next) return;
      step(`plays ${value}`);
      advance(next);
    },
    tap(card) {
      const g = state.game;
      if (!g || dealing) return;
      if (g.phase === 'give' && g.declarer === 0) {
        const to = giveTo(g);
        const next = giveCard(g, 0, card);
        if (next) gave(next, 0, to, card);
        return;
      }
      if (g.phase === 'play' && g.turn === 0) {
        if (!playCard(g, 0, card)) {
          step(`card refused ${cardKey(card)}`);
          return;
        }
        set({ playedByPlayer: state.playedByPlayer + 1 });
        advance(played(g, 0, card));
      }
    },
    stop() {
      clearTimer();
      stopClock();
    },
    load(g) {
      clearTimer();
      dealing = false;
      set({ collecting: false, demo: true, lastGift: null });
      advance(g);
    },
    backdate(ms) {
      set({ startedAt: state.startedAt - ms });
    },
  };
}

let current: Store1000 | null = null;
export const set1000Store = (s: Store1000) => {
  current = s;
};
export const store1000 = (): Store1000 => {
  if (!current) throw new Error('store1000: not created');
  return current;
};
export function use1000(): TableState {
  const s = store1000();
  return useSyncExternalStore(s.subscribe, s.getState, s.getState);
}

/** The table as text for a report: hands (the computer's too: a report is for us), the bidding, the trick, the totals. */
export function boardText1000(st: TableState): Record<string, unknown> | null {
  const g = st.game;
  if (!g) return null;
  const k = (cards: readonly HCard[]) => cards.map(cardKey).join(' ');
  return {
    deal: g.handNo + 1, target: g.target, phase: g.phase, dealer: g.dealer, declarer: g.declarer, bids: g.bids, bid: g.bid, contract: g.contract,
    trump: g.trump, melds: g.melds.map((m) => `${m.seat}:${m.suit}`).join(' '), turn: g.turn,
    names: (st.names ?? fixedNames(t)).name.slice(1, 3).join(', '), hands: g.hands.map(k), musik: k(g.musikCards), given: k(g.given),
    trick: g.trick.map((p) => `${p.seat}:${cardKey(p.card)}`).join(' '), taken: g.taken, totals: g.scores,
    history: g.history.map((r) => `${r.contract}@${r.declarer} ${r.points.join('/')}`), pattern: g.pattern.join(','),
  };
}
